import React, { FC, useContext, useEffect, useState } from "react";
import { ClientAutoRedactionState } from "src/common/utils";
import OrganizationAPI from "../API/OrganizationAPI";
import DatasetAPI from "./DatasetAPI";
import { useApi } from "../../../Api/useApi";
import Spinner from "src/components/common/Spinner";
import { Page } from "src/components/common";
import { Button, Form, Radio } from "antd";
import { FormComponentProps } from "antd/lib/form";
import AuthorizationWrapper from "src/common/AuthorizationWrapper";
import { AdminPermissions } from "src/common/Permissions";
import DatasetContext from "src/context/DatasetContext";
import { Dataset } from "src/@types/ImpiricaPlatformApi";
import { toast } from "react-toastify";

interface IProps extends FormComponentProps {
	match?: any;
	history?: any;
}

const defaultDataset: DatasetDTO = {
	id: -1,
	name: "",
	isActive: true,
	training: false,
	automaticClientRedaction: ClientAutoRedactionState.Inherit,
	productId: undefined,
};

const OrganizationDatasetPII: FC<IProps> = ({
	match,
	form: { getFieldsError, validateFields },
}) => {
	const { modulesClient, datasetsClient } = useApi();

	const [dataset, setDataset] = useState<DatasetDTO>(defaultDataset);
	const [organization, setOrganization] = useState<OrganizationDTO>(
		{} as OrganizationDTO
	);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [saving, setSaving] = useState<boolean>(false);
	const [formIsValid, setFormIsValid] = useState<boolean>(false);

	const editingDataset =
		match.params.datasetId !== undefined && match.params.datasetId > 0;

	useEffect(() => {
		const {
			datasetId,
			organizationId,
		}: { datasetId: number; organizationId: number } = match.params;

		if (modulesClient) {
			_loadInitialState(organizationId, datasetId);
		}
	}, [modulesClient]);

	useEffect(() => {
		validateFields();
		_checkErrors();
	}, [dataset]);

	useEffect(() => {
		validateFields();
		_checkErrors();
	}, [isLoading]);

	const _loadInitialState = async (orgId: number, datasetId: number) => {
		let loadedOrganization = await OrganizationAPI.getOrganization({
			id: orgId,
		});
		setOrganization(loadedOrganization);

		if (editingDataset && modulesClient) {
			try {
				let loadedDataset = await DatasetAPI.getOrganizationDataset(
					orgId,
					datasetId
				);

				setDataset(loadedDataset);
			} catch (ex) {
				console.log(ex);
			}
		}

		setIsLoading(false);
	};

	const _checkErrors = () => {
		const fieldsErrors = getFieldsError();

		let keys = Object.keys(fieldsErrors);
		if (keys.length <= 0) return;

		// check if any of the values are undefined
		let hasErrors = keys.some(
			(field) =>
				fieldsErrors[field] !== undefined && fieldsErrors[field]!.length > 0
		);
		setFormIsValid(!hasErrors);
	};

	const _generateToastSuccessMessage = () => {
		return "Successfully updated dataset PII fields";
	};

	const _onRadioChanged = (e: any) => {
		const updatedDataset = {
			...dataset,
			[e.target.name]: e.target.value,
		};
		setDataset(updatedDataset);
	};

	const _submitDatasetForm = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		_submitDatasetPIIFields(dataset);
	};

	const _submitDatasetPIIFields = async (datasetToSubmit: DatasetDTO) => {
		if (organization.automaticDataLock && !dataset.automaticDataLock) {
			toast.error(
				"Current organization setting doesn't allow for no data lock"
			);
			return;
		}
		if (
			organization.automaticClientRedaction &&
			!dataset.automaticClientRedaction
		) {
			toast.error(
				"Current organization setting doesn't allow for no redaction"
			);
			return;
		}

		let upsertRequest = { ...datasetToSubmit } as Dataset;

		setSaving(true);
		const toastSuccessMessage = _generateToastSuccessMessage();

		try {
			if (datasetsClient) {
				await datasetsClient.updateDatasetPIIFields(
					upsertRequest,
					match.params.organizationId.toString(),
					match.params.datasetId.toString()
				);
			}

			toast.success(toastSuccessMessage);
		} catch (e) {
			console.error(e);
			toast.error("Something went wrong!");
		} finally {
			setSaving(false);
		}
	};

	if (isLoading) return <Spinner />;

	return isLoading ? (
		<Spinner />
	) : (
		<Page title="Dataset Details">
			<Form
				onSubmit={(e: any) => _submitDatasetForm(e)}
				onChange={_checkErrors}
			>
				<div className="container-fluid">
					<div className="row">
						<div className="col-3 form-group">
							<label>First Name</label>
							<Form.Item>
								<AuthorizationWrapper
									type="disable"
									permissions={[AdminPermissions.EditDataset]}
								>
									<Radio.Group
										value={dataset.showClientFirstName}
										name="showClientFirstName"
										onChange={(e) => _onRadioChanged(e)}
									>
										<Radio value={true} checked={dataset.showClientFirstName}>
											Show
										</Radio>
										<Radio value={false} checked={!dataset.showClientFirstName}>
											Hide
										</Radio>
									</Radio.Group>
								</AuthorizationWrapper>
							</Form.Item>
						</div>
					</div>
					<div className="row">
						<div className="col-3 form-group">
							<label>Last Name</label>
							<Form.Item>
								<AuthorizationWrapper
									type="disable"
									permissions={[AdminPermissions.EditDataset]}
								>
									<Radio.Group
										value={dataset.showClientLastName}
										name="showClientLastName"
										onChange={(e) => _onRadioChanged(e)}
									>
										<Radio value={true} checked={dataset.showClientLastName}>
											Show
										</Radio>
										<Radio value={false} checked={!dataset.showClientLastName}>
											Hide
										</Radio>
									</Radio.Group>
								</AuthorizationWrapper>
							</Form.Item>
						</div>
					</div>
					<div className="row">
						<div className="col-3 form-group">
							<label>Employee Id/Id</label>
							<Form.Item>
								<AuthorizationWrapper
									type="disable"
									permissions={[AdminPermissions.EditDataset]}
								>
									<Radio.Group
										value={dataset.showClientParticipantId}
										name="showClientParticipantId"
										onChange={(e) => _onRadioChanged(e)}
									>
										<Radio
											value={true}
											checked={dataset.showClientParticipantId}
										>
											Show
										</Radio>
										<Radio
											value={false}
											checked={!dataset.showClientParticipantId}
										>
											Hide
										</Radio>
									</Radio.Group>
								</AuthorizationWrapper>
							</Form.Item>
						</div>
					</div>
					<div className="row">
						<div className="col-3 form-group">
							<label>Birth Month</label>
							<Form.Item>
								<AuthorizationWrapper
									type="disable"
									permissions={[AdminPermissions.EditDataset]}
								>
									<Radio.Group
										value={dataset.showClientBirthMonth}
										name="showClientBirthMonth"
										onChange={(e) => _onRadioChanged(e)}
									>
										<Radio value={true} checked={dataset.showClientBirthMonth}>
											Show
										</Radio>
										<Radio
											value={false}
											checked={!dataset.showClientBirthMonth}
										>
											Hide
										</Radio>
									</Radio.Group>
								</AuthorizationWrapper>
							</Form.Item>
						</div>
					</div>
					<div className="row">
						<div className="col-3 form-group">
							<label>Birth Day</label>
							<Form.Item>
								<AuthorizationWrapper
									type="disable"
									permissions={[AdminPermissions.EditDataset]}
								>
									<Radio.Group
										value={dataset.showClientBirthDay}
										name="showClientBirthDay"
										onChange={(e) => _onRadioChanged(e)}
									>
										<Radio value={true} checked={dataset.showClientBirthDay}>
											Show
										</Radio>
										<Radio value={false} checked={!dataset.showClientBirthDay}>
											Hide
										</Radio>
									</Radio.Group>
								</AuthorizationWrapper>
							</Form.Item>
						</div>
					</div>
					<div className="row">
						<div className="col-3 form-group">
							<label>Address</label>
							<Form.Item>
								<AuthorizationWrapper
									type="disable"
									permissions={[AdminPermissions.EditDataset]}
								>
									<Radio.Group
										value={dataset.showClientAddress}
										name="showClientAddress"
										onChange={(e) => _onRadioChanged(e)}
									>
										<Radio value={true} checked={dataset.showClientAddress}>
											Show
										</Radio>
										<Radio value={false} checked={!dataset.showClientAddress}>
											Hide
										</Radio>
									</Radio.Group>
								</AuthorizationWrapper>
							</Form.Item>
						</div>
					</div>
					<div className="row">
						<div className="col">
							<DatasetContext.Consumer>
								{(context) => (
									<div>
										<AuthorizationWrapper
											type="hide"
											permissions={[AdminPermissions.EditDataset]}
										>
											<Button type="primary" htmlType="submit" loading={saving}>
												Save
											</Button>
										</AuthorizationWrapper>
									</div>
								)}
							</DatasetContext.Consumer>
						</div>
					</div>
				</div>
			</Form>
		</Page>
	);
};

export default Form.create<IProps>()(OrganizationDatasetPII);
